const RENTAL_DOCUMENT_TYPE = "VHO";

const RENTAL_PERIODS = [
  { id: "halve_dag", label: "Halve dag", prijsKey: "prijs5" },
  { id: "dag", label: "Dag", prijsKey: "prijs6" },
  { id: "weekend", label: "Weekend", prijsKey: "prijs7" },
  { id: "week", label: "Week", prijsKey: "prijs8" },
  { id: "maand", label: "Maand", prijsKey: "prijs9" },
];

const RENTAL_NUMBER = /^RNT/i;

function isRentalArticle(nummer) {
  return RENTAL_NUMBER.test(String(nummer || "").trim());
}

function extractPrijstabel(product) {
  let raw = product?.prijstabel;
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      raw = null;
    }
  }
  if (Array.isArray(raw)) {
    return raw.find((row) => row && typeof row === "object") || {};
  }
  if (raw && typeof raw === "object") {
    return raw;
  }
  return {};
}

function readPrice(row, key) {
  const value = Number(row?.[key]);
  return Number.isFinite(value) ? value : 0;
}

function verkoopprijs(product, { isTopklant = false } = {}) {
  const row = extractPrijstabel(product);
  return readPrice(row, isTopklant ? "prijs2" : "prijs1");
}

function periodPrice(product, periodId) {
  const period = RENTAL_PERIODS.find((item) => item.id === periodId);
  if (!period) {
    return null;
  }
  return readPrice(extractPrijstabel(product), period.prijsKey);
}

function handlingCost(product) {
  return readPrice(extractPrijstabel(product), "prijs4");
}

function depositAmount(product) {
  return readPrice(extractPrijstabel(product), "prijs10");
}

function groupKey(group) {
  return String(group?._id || group?.id || "");
}

function defaultVariantSelections(groups = []) {
  const selections = {};
  for (const group of groups) {
    const defaults = (group.options || [])
      .filter((option) => option.isDefault)
      .map((option) => option.nummer);
    // Eén keuze kan maar één optie voorselecteren. Meerdere defaults blijven
    // bewaard; de eerste wordt de startkeuze.
    selections[groupKey(group)] =
      group.selection === "single" ? defaults.slice(0, 1) : defaults;
  }
  return selections;
}

function validateVariantSelection(groups = [], selectionsByGroupId = {}) {
  const errors = [];
  for (const group of groups) {
    const key = groupKey(group);
    const chosen = Array.isArray(selectionsByGroupId[key])
      ? selectionsByGroupId[key]
      : [];
    const allowed = new Set((group.options || []).map((option) => option.nummer));
    if (chosen.some((nummer) => !allowed.has(nummer))) {
      errors.push(`${group.label}: onbekende optie`);
    }
    if (group.selection === "single" && chosen.length > 1) {
      errors.push(`${group.label}: kies één optie`);
    }
    if (group.required && chosen.length === 0) {
      errors.push(`${group.label}: verplichte keuze`);
    }
  }
  return errors;
}

function linesForArticle({ artNr, quantity, periodId, isTopklant, product }) {
  if (isRentalArticle(artNr)) {
    const period = RENTAL_PERIODS.find((item) => item.id === periodId);
    return [
      {
        artNr,
        quantity,
        remark: period ? period.label : null,
        fixedPrice: periodPrice(product, periodId) ?? 0,
        kind: "huur",
      },
      {
        artNr,
        quantity,
        remark: "Behandelingskost",
        fixedPrice: handlingCost(product),
        kind: "behandelingskost",
      },
      {
        artNr,
        quantity,
        remark: "Borg",
        fixedPrice: depositAmount(product),
        kind: "borg",
      },
    ];
  }

  return [
    {
      artNr,
      quantity,
      remark: null,
      fixedPrice: verkoopprijs(product, { isTopklant }),
      kind: "verkoop",
    },
  ];
}

/**
 * Parent plus gekozen opties.
 * RNT: periodeprijs, plus behandelingskost en borg als extra lijnen.
 * Ander artikel: verkoopprijs (prijs2 bij topklant).
 * Hoeveelheid van het toestel geldt ook voor de opties.
 */
function buildRentalOrderLines({
  items = [],
  periodId,
  isTopklant = false,
  productsByNummer = {},
} = {}) {
  const lines = [];
  for (const item of items) {
    const quantity = item.quantity;
    const parent = productsByNummer[item.artNr];
    lines.push(
      ...linesForArticle({
        artNr: item.artNr,
        quantity,
        periodId,
        isTopklant,
        product: parent,
      }),
    );

    const seen = new Set();
    for (const option of item.options || []) {
      const nummer = option.artNr;
      if (!nummer || seen.has(nummer)) {
        continue;
      }
      seen.add(nummer);
      lines.push(
        ...linesForArticle({
          artNr: nummer,
          quantity,
          periodId,
          isTopklant,
          product: productsByNummer[nummer],
        }),
      );
    }
  }
  return lines;
}

function orderHasRentalArticle(items = []) {
  return items.some(
    (item) =>
      isRentalArticle(item.artNr) ||
      (item.options || []).some((option) => isRentalArticle(option.artNr)),
  );
}

module.exports = {
  RENTAL_DOCUMENT_TYPE,
  RENTAL_PERIODS,
  isRentalArticle,
  extractPrijstabel,
  verkoopprijs,
  periodPrice,
  handlingCost,
  depositAmount,
  defaultVariantSelections,
  validateVariantSelection,
  buildRentalOrderLines,
  orderHasRentalArticle,
};
